import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

import {
  AcceptRegistrationItemDocument,
  GetOpenRegistrationCountDocument,
  GetRegistrationItemsDocument,
  RegistrationDetailItemDataFragment,
  RejectRegistrationItemDocument,
  UpdateRegistrationPersonEmailDocument,
} from '../../types/api/graphql';

/** Both transitions change the list and the number in the menu. */
const refetchQueries = [
  GetRegistrationItemsDocument,
  GetOpenRegistrationCountDocument,
];

/**
 * Accept or reject one registration.
 *
 * Both transitions are final and the server refuses a second attempt on a
 * request that was already handled, so callers must surface the thrown error
 * rather than swallow it. Both refresh the list and the menu badge; the
 * detail itself updates through the returned registration, which the cache
 * normalises onto the same `Registration` it already holds.
 */
export function useRegistrationHandling() {
  const [acceptRegistrationItem, { loading: acceptRegistrationItemLoading }] =
    useMutation(AcceptRegistrationItemDocument, { refetchQueries });

  const [rejectRegistrationItem, { loading: rejectRegistrationItemLoading }] =
    useMutation(RejectRegistrationItemDocument, { refetchQueries });

  const [
    updateRegistrationPersonEmail,
    { loading: updateRegistrationPersonEmailLoading },
  ] = useMutation(UpdateRegistrationPersonEmailDocument);

  const accept = useCallback(
    async (
      registrationId: string
    ): Promise<RegistrationDetailItemDataFragment | undefined> => {
      const result = await acceptRegistrationItem({
        variables: { registrationId },
      });

      return (
        (result.data?.registrationAccept
          .registration as RegistrationDetailItemDataFragment) ?? undefined
      );
    },
    [acceptRegistrationItem]
  );

  const reject = useCallback(
    async (
      registrationId: string,
      reason: string
    ): Promise<RegistrationDetailItemDataFragment | undefined> => {
      const result = await rejectRegistrationItem({
        variables: { registrationId, reason },
      });

      return (
        (result.data?.registrationReject
          .registration as RegistrationDetailItemDataFragment) ?? undefined
      );
    },
    [rejectRegistrationItem]
  );

  /**
   * Fix the address of one person before accepting, so a taken address does
   * not force the household to register again. Refused by the server when
   * the address is invalid or already in use; callers surface that error.
   */
  const updateEmail = useCallback(
    async (
      registrationPersonId: string,
      email: string
    ): Promise<RegistrationDetailItemDataFragment | undefined> => {
      const result = await updateRegistrationPersonEmail({
        variables: { registrationPersonId, email },
      });

      return (
        (result.data?.registrationPersonEmailUpdate
          .registration as RegistrationDetailItemDataFragment) ?? undefined
      );
    },
    [updateRegistrationPersonEmail]
  );

  return {
    accept,
    reject,
    updateEmail,
    loading:
      acceptRegistrationItemLoading ||
      rejectRegistrationItemLoading ||
      updateRegistrationPersonEmailLoading,
  };
}
